import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
} from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';

import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { IOrganization } from '@/types/admin/organizations';
import { ORGANIZATION_TABS } from '@/constants/admin';
import { useOrganizationForm } from '@/hooks/admin/useOrganizationForm';
import {
  useLazyGetOrganizationPropertiesQuery,
  useLazyGetOrganizationAdminsQuery,
} from '@/store/api/admin/organizationsApi';
import { BaseAdminScreen } from '@/components';
import { MobileFormScreen } from '@/components/common/admin/mobile/MobileFormScreen';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import {
  OrganizationInfoSection,
  PaymentAccountsSection,
} from '@/components/admin/organizations/form-sections';
import { OrganizationPropertiesTab } from '@/components/admin/organizations/desktop/OrganizationPropertiesTab';
import { OrganizationUsersTab } from '@/components/admin/organizations/desktop/OrganizationUsersTab';

type EditOrganizationScreenProps = StackScreenProps<AdminStackParamList, 'EditOrganization'>;

export const EditOrganizationScreen: React.FC<EditOrganizationScreenProps> = ({
  navigation,
  route,
}) => {
  const { organization } = route.params as { organization: IOrganization };
  const styles = useStyles();

  const {
    formData,
    errors,
    isUpdating,
    updateField,
    updatePaymentAccount,
    updateOrganization,
    removeOrganization,
  } = useOrganizationForm({ organization, onClose: () => navigation.goBack() });

  const [activeTab, setActiveTab] = useState(0);

  const [getProperties, { data: propertiesData, isLoading: isLoadingProperties }] =
    useLazyGetOrganizationPropertiesQuery();
  const [getAdmins, { data: adminsData, isLoading: isLoadingAdmins }] =
    useLazyGetOrganizationAdminsQuery();

  const handleTabChange = useCallback(
    (index: number) => {
      setActiveTab(index);
      if (index === 1) getProperties(organization.id);
      else if (index === 2) getAdmins(organization.id);
    },
    [organization.id, getProperties, getAdmins],
  );

  const tabBar = (
    <View style={styles.tabs}>
      {ORGANIZATION_TABS.map((tab, index) => (
        <TouchableOpacity
          key={tab}
          style={[styles.tab, activeTab === index && styles.activeTab]}
          onPress={() => handleTabChange(index)}
        >
          <Text style={[styles.tabText, activeTab === index && styles.activeTabText]}>
            {tab}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  if (activeTab === 0) {
    return (
      <MobileFormScreen
        screenName="EditOrganization"
        navigation={navigation}
        isSubmitting={isUpdating}
        submitText="Save"
        onSubmit={updateOrganization}
        onCancel={() => navigation.goBack()}
        showDelete
        onDelete={removeOrganization}
        topSlot={tabBar}
      >
        <OrganizationInfoSection
          name={formData.name}
          domain={formData.domain}
          nameError={errors.name}
          domainError={errors.domain}
          onNameChange={(text) => updateField('name', text)}
          onDomainChange={(text) => updateField('domain', text.toLowerCase())}
          disabled={isUpdating}
        />

        <PaymentAccountsSection
          paymentAccounts={formData.paymentAccounts}
          onPaymentAccountChange={updatePaymentAccount}
          disabled={isUpdating}
        />
      </MobileFormScreen>
    );
  }

  return (
    <BaseAdminScreen
      screenName="EditOrganization"
      navigation={navigation}
      showPlaceholder={false}
      scrollEnabled={false}
    >
      {tabBar}

      <View style={styles.tabContent}>
        {activeTab === 1 && (
          <OrganizationPropertiesTab
            organizationId={organization.id}
            properties={propertiesData?.response || []}
            isLoading={isLoadingProperties}
          />
        )}

        {activeTab === 2 && (
          <OrganizationUsersTab
            organizationId={organization.id}
            admins={adminsData?.response || []}
            isLoading={isLoadingAdmins}
          />
        )}
      </View>
    </BaseAdminScreen>
  );
};

const useStyles = () =>
  useThemedStyles(({ components }) =>
    StyleSheet.create({
      tabs: {
        flexDirection: 'row',
        backgroundColor: components.admin.dataTable.bg,
        marginHorizontal: 16,
        marginVertical: 12,
        borderRadius: 8,
        padding: 4,
      },
      tab: {
        flex: 1,
        paddingVertical: 10,
        paddingHorizontal: 12,
        borderRadius: 6,
        alignItems: 'center',
      },
      activeTab: {
        backgroundColor: PRIMITIVE_COLORS.resioBlue,
      },
      tabText: {
        fontSize: 13,
        fontWeight: '500',
        color: components.admin.dataCell.textMuted,
      },
      activeTabText: {
        color: '#FFFFFF',
      },
      tabContent: {
        flex: 1,
      },
    }),
  );
